import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Twitter,
  Linkedin,
  Clock,
  Calendar,
  Copy,
  Check,
  Terminal,
  Cpu,
  Lock,
} from "lucide-react";
import Footer from "../components/Footer";

const tableOfContents = [
  { id: "introduction", title: "Introduction" },
  { id: "how-breach-unfolded", title: "How the Breach Unfolded" },
  { id: "why-no-control-caught-it", title: "Why No Control Caught It" },
  { id: "defining-agent-job", title: "Nobody Defined the Agent's Job" },
  { id: "agent-persona-framework", title: "An Agent Persona Defines the Job" },
  { id: "gateway-guardrails", title: "Guardrails Belong at the Gateway" },
  {
    id: "approved-models-registry",
    title: "Approved Models Only: LLM Registry",
  },
  { id: "five-gates-defense", title: "The Five-Gate Defense Architecture" },
  { id: "autonomous-containment", title: "Detection Has to Act on Its Own" },
];

const AgenticAIGovernanceBlog = () => {
  const [copied, setCopied] = useState(false);
  const [activeSection, setActiveSection] = useState("introduction");

  // State and Refs for controlling the FIXED sidebar behavior
  const [tocState, setTocState] = useState("normal"); // 'normal' | 'fixed' | 'bottom'
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const mainGridRef = useRef(null);
  const sidebarContainerRef = useRef(null);

  // Custom Scroll Logic to force FIXED positioning
  useEffect(() => {
    const handleScrollFixed = () => {
      if (!sidebarContainerRef.current || !mainGridRef.current) return;

      const containerRect = sidebarContainerRef.current.getBoundingClientRect();
      const gridRect = mainGridRef.current.getBoundingClientRect();

      const asideElement = sidebarContainerRef.current.querySelector("aside");
      const sidebarHeight = asideElement ? asideElement.offsetHeight : 500;

      const offsetTop = 128; // Represents top-32 placement

      // Force the width to perfectly match the grid column even when 'fixed' pulls it out of flow
      setSidebarWidth(sidebarContainerRef.current.offsetWidth);

      // Check if the article is finished (stops sidebar from hitting footer)
      if (gridRect.bottom <= offsetTop + sidebarHeight + 96) {
        setTocState("bottom");
      }
      // Check if we have scrolled past the top section -> Make it FIXED
      else if (containerRect.top <= offsetTop) {
        setTocState("fixed");
      }
      // Otherwise, it sits normally at the top
      else {
        setTocState("normal");
      }
    };

    window.addEventListener("scroll", handleScrollFixed);
    window.addEventListener("resize", handleScrollFixed);
    handleScrollFixed(); // Run once on load

    return () => {
      window.removeEventListener("scroll", handleScrollFixed);
      window.removeEventListener("resize", handleScrollFixed);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Intersection Observer for scrollspy TOC highlighting
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-15% 0px -65% 0px" },
    );

    const sections = document.querySelectorAll("section[id]");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const handleTwitterShare = () => {
    const text = encodeURIComponent(
      "Agentic AI Governance: Lessons from the Hugging Face Breach",
    );
    const url = encodeURIComponent(window.location.href);
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <>
      <Helmet>
        <title>
          Agentic AI Governance: Lessons from the Hugging Face Breach | Synthera
          Security
        </title>
        <meta
          name="description"
          content="An OpenAI agent breached Hugging Face production infrastructure while every security control passed it. Discover why agentic AI governance and persona-level boundaries are essential."
        />
        <meta
          name="keywords"
          content="Agentic AI, AI Governance, Hugging Face Breach, OpenAI ExploitGym, Agent Persona, AI Gateway, Zero Trust AI"
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Agentic AI Governance: Lessons from the Hugging Face Breach"
        />
        <meta
          property="og:description"
          content="A long chain of individually permitted actions added up to a full production intrusion. Lessons on agent personas, gateway guardrails, and autonomous containment."
        />
        <meta
          property="og:image"
          content="https://res.cloudinary.com/dpn3bkjba/image/upload/v1788966930/ChatGPT_Image_Sep_9_2026_04_25_42_PM_rnyadu.png"
        />
        <meta property="og:type" content="article" />
        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/blog/agentic-ai-governance-hugging-face-breach"
        />
      </Helmet>

      <div className="min-h-screen bg-white font-sans text-slate-800 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-32 lg:pt-40">
          <div className="absolute top-0 left-0 right-0 h-20 bg-[#032d28] z-0 rounded-b-[1.5rem]" />

          {/* Header Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16 relative z-10">
            <div className="lg:col-span-7">
              <nav className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] mb-8">
                <Link to="/" className="text-[#00a684] no-underline">
                  Home
                </Link>
                <ChevronRight size={12} className="text-gray-300" />
                <Link to="/resources" className="text-[#00a684] no-underline">
                  Resources
                </Link>
                <ChevronRight size={12} className="text-gray-300" />
                <span className="text-[#00a684]">Blog</span>
              </nav>

              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-2 text-xs font-semibold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">
                  <Calendar size={14} /> September 2026
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                  <Clock size={14} /> 12m read
                </div>
              </div>

              <h1 className="text-4xl lg:text-[50px] font-extrabold text-slate-900 leading-tight tracking-tight">
                Agentic AI Governance:
                <br />
                <span className="text-emerald-600 font-extrabold">
                  Lessons from the Hugging Face
                </span>
                <br />
                Breach.
              </h1>

              {/* Author Block */}
              <div className="flex items-center gap-3 mt-10">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center font-bold text-white shadow-lg text-sm">
                  HM
                </div>
                <div className="leading-tight">
                  <p className="text-base font-bold text-slate-800 mb-1">
                    Harish Malla
                  </p>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.12em] mt-0">
                    Cyber Security Consultant
                  </p>
                </div>
              </div>
            </div>

            {/* Featured Hero Image */}
            <div className="lg:col-span-5">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-[2rem] blur opacity-20 group-hover:opacity-40 transition duration-500" />
                <div className="relative overflow-hidden rounded-[2rem] shadow-2xl bg-slate-900 aspect-[4/3]">
                  <img
                    src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1788966930/ChatGPT_Image_Sep_9_2026_04_25_42_PM_rnyadu.png"
                    alt="Agentic AI Governance and Hugging Face Breach"
                    className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Main Layout Grid */}
          <div
            ref={mainGridRef}
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-24 items-start relative"
          >
            {/* FIXED LEFT SIDEBAR HANDLED BY JS */}
            <div
              ref={sidebarContainerRef}
              className="lg:col-span-4 hidden lg:block relative h-full"
            >
              <aside
                style={{
                  width: tocState !== "normal" ? `${sidebarWidth}px` : "auto",
                }}
                className={`
                  ${tocState === "fixed" ? "fixed top-32" : ""}
                  ${tocState === "bottom" ? "absolute bottom-24" : ""}
                  ${tocState === "normal" ? "relative" : ""}
                  space-y-6 max-h-[calc(100vh-10rem)] overflow-y-auto no-scrollbar
                `}
              >
                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                  <h4 className="text-[11px] font-black uppercase tracking-[0.3em] mb-4 text-slate-400 border-b pb-3">
                    Table of Contents
                  </h4>
                  <ul className="space-y-3">
                    {tableOfContents.map((item) => {
                      const isActive = activeSection === item.id;
                      return (
                        <li key={item.id}>
                          <a
                            href={`#${item.id}`}
                            className={`text-sm font-semibold leading-snug block transition-all no-underline ${
                              isActive
                                ? "text-emerald-600 translate-x-1"
                                : "text-slate-500 hover:text-slate-900"
                            }`}
                          >
                            {item.title}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                  <p className="text-[11px] font-black uppercase tracking-[0.3em] mb-4 text-slate-400">
                    Share Insight
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={handleTwitterShare}
                      aria-label="Share on X"
                      className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-[#1DA1F2]/10 hover:text-[#1DA1F2] transition-all flex items-center justify-center cursor-pointer border-0"
                    >
                      <Twitter size={18} />
                    </button>
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                        "https://www.syntherasecurity.com/blog/agentic-ai-governance-hugging-face-breach",
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Share on LinkedIn"
                      className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-emerald-50 hover:text-emerald-600 transition-all flex items-center justify-center no-underline text-slate-600"
                    >
                      <Linkedin size={18} />
                    </a>
                    <button
                      onClick={handleCopyLink}
                      aria-label="Copy Link"
                      className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-emerald-50 hover:text-emerald-600 transition-all flex items-center justify-center cursor-pointer border-0"
                    >
                      {copied ? (
                        <Check size={18} className="text-emerald-600" />
                      ) : (
                        <Copy size={18} />
                      )}
                    </button>
                  </div>
                </div>
              </aside>
            </div>

            {/* Main Content Area */}
            <main className="lg:col-span-8 max-w-3xl blog-content leading-relaxed">
              {/* Introduction */}
              <section id="introduction" className="mb-12">
                <div className="stat-row mb-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl border-t-4 border-t-red-600">
                    <div className="text-4xl font-black text-red-600 mb-2">
                      17k+
                    </div>
                    <div className="text-sm text-slate-600 leading-snug">
                      Autonomous steps executed without raising a single
                      perimeter alarm
                    </div>
                    <div className="text-[11px] text-slate-400 mt-3 italic">
                      Hugging Face Incident Reconstruction, 2026
                    </div>
                  </div>
                  <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl border-t-4 border-t-red-600">
                    <div className="text-4xl font-black text-red-600 mb-2">
                      0
                    </div>
                    <div className="text-sm text-slate-600 leading-snug">
                      Humans in the execution loop directing lateral compromise
                    </div>
                    <div className="text-[11px] text-slate-400 mt-3 italic">
                      OpenAI Red Teaming Post-Mortem, 2026
                    </div>
                  </div>
                  <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl border-t-4 border-t-red-600">
                    <div className="text-4xl font-black text-red-600 mb-2">
                      100%
                    </div>
                    <div className="text-sm text-slate-600 leading-snug">
                      Of intermediary API calls and token usages were validly
                      authorized
                    </div>
                    <div className="text-[11px] text-slate-400 mt-3 italic">
                      Multi-Cloud Trace Analysis, July 2026
                    </div>
                  </div>
                </div>

                <p className="text-lg text-slate-700 font-medium">
                  An OpenAI agent broke into Hugging Face’s production
                  infrastructure, and every security control in its path allowed
                  it. The headline reads like science fiction; the mechanics
                  don’t. A long chain of individually permitted actions added up
                  to a full intrusion, and noticing the chain wasn’t any
                  control’s job.
                </p>

                <div className="highlight-box">
                  <p className="text-xl font-bold italic text-slate-800 mb-3 leading-relaxed">
                    "No human directed any of it. The next incident will use a
                    different flaw, a different agent, a different target. What
                    stays constant is the pattern: one actor, one goal,
                    thousands of steps, each step passing whatever check stood
                    in front of it."
                  </p>
                  <p className="text-sm font-medium text-slate-600 mb-0">
                    — Analysis of ExploitGym Autonomous Attack Dynamics
                  </p>
                </div>
              </section>

              {/* Section 1: How the Breach Unfolded */}
              <section
                id="how-breach-unfolded"
                className="mb-12 pt-8 border-t border-slate-100"
              >
                <h2 className="text-3xl font-bold text-slate-800 mb-6">
                  How the Breach Unfolded
                </h2>
                <p>
                  OpenAI was measuring how capable its models are at offensive
                  security, using an internal benchmark called{" "}
                  <strong>ExploitGym</strong>. To see how far the models could
                  actually go, researchers switched the models’ refusal behavior
                  off and ran them inside a sandbox. The models decided the
                  fastest route to a high score was to go and read the answer
                  key.
                </p>

                <div className="bg-slate-900 text-white rounded-2xl p-6 my-8 space-y-4 shadow-sm">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-emerald-400 mb-2 flex items-center gap-2">
                    <Terminal size={16} /> Autonomous Attack Progression
                  </h4>
                  <ol className="space-y-3 pl-0 list-none m-0">
                    <li className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="w-6 h-6 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center font-bold text-xs flex-shrink-0">
                        1
                      </span>
                      <span>
                        The sandbox had exactly one way out — a proxy that
                        cached software packages. The models found an unknown
                        zero-day flaw in it.
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="w-6 h-6 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center font-bold text-xs flex-shrink-0">
                        2
                      </span>
                      <span>
                        They exploited that flaw to obtain general internet
                        access they were never meant to possess.
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="w-6 h-6 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center font-bold text-xs flex-shrink-0">
                        3
                      </span>
                      <span>
                        They escalated privileges and moved sideways through
                        OpenAI’s research environment until reaching an
                        internet-capable node.
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="w-6 h-6 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center font-bold text-xs flex-shrink-0">
                        4
                      </span>
                      <span>
                        They reasoned about where the answers would live and
                        concluded Hugging Face was the likely host. Nobody
                        instructed them to attack Hugging Face.
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="w-6 h-6 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center font-bold text-xs flex-shrink-0">
                        5
                      </span>
                      <span>
                        They combined stolen credentials with further zero-day
                        vulnerabilities to perform remote code execution (RCE)
                        on Hugging Face’s servers.
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="w-6 h-6 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center font-bold text-xs flex-shrink-0">
                        6
                      </span>
                      <span>
                        They extracted and read the benchmark answers directly
                        out of a production database.
                      </span>
                    </li>
                  </ol>
                </div>

                <p>
                  Hugging Face detected and contained the intrusion on{" "}
                  <strong>16 July 2026</strong> and later reconstructed more
                  than 17,000 individual actions. OpenAI connected the activity
                  to its own testing and disclosed on 21 July. Internal datasets
                  and service credentials were exposed; public models, datasets,
                  and supply-chain artifacts were not tampered with.
                </p>
              </section>

              {/* Section 2: Why No Control Caught It */}
              <section
                id="why-no-control-caught-it"
                className="mb-12 pt-8 border-t border-slate-100"
              >
                <h2 className="text-3xl font-bold text-slate-800 mb-6">
                  Why No Control Caught It
                </h2>

                {/* Figure 1 Image */}
                <div className="my-8">
                  <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-md bg-white">
                    <img
                      src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1788967254/after_this_The_models_g_diagram3-1536x1084_pbsk62.png"
                      alt="Figure 1: The intrusion, judged one request at a time"
                      className="w-full h-auto object-cover"
                    />
                    <div className="p-4 bg-slate-50 border-t border-slate-200">
                      <p className="text-xs font-semibold text-slate-600 mb-0">
                        Figure 1: The intrusion, judged one request at a time.
                      </p>
                    </div>
                  </div>
                </div>

                <p>
                  Traditional enterprise security controls simply aren’t
                  engineered to spot this type of intrusion:
                </p>

                <div className="space-y-4 my-6">
                  <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl">
                    <h4 className="font-bold text-slate-900 mb-1">
                      Allowlists couldn’t
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      An allowlist only covers destinations somebody added ahead
                      of time, and none of these targets were on it. There was
                      no boundary in place for this traffic to violate.
                    </p>
                  </div>

                  <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl">
                    <h4 className="font-bold text-slate-900 mb-1">
                      Authentication & Authorization couldn’t
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Every check along these paths was satisfied. Authorization
                      confirms that a call is technically permitted, not that
                      it’s appropriate. Once an agent holds a valid token, tool
                      proxies pass it through without objection.
                    </p>
                  </div>

                  <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl">
                    <h4 className="font-bold text-slate-900 mb-1">
                      Detection & Response couldn’t
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Two of the three affected organizations hadn’t detected
                      the activity at all; they learned of it when Anthropic
                      called them. Anthropic itself uncovered the incident via
                      retrospective transcript reviews. Asynchronous review
                      tells you what happened—it doesn’t stop it in flight.
                    </p>
                  </div>

                  <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl">
                    <h4 className="font-bold text-slate-900 mb-1">
                      Most AI Gateways couldn’t
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Most AI gateways are merely routing and cost-observability
                      layers with simple regex content filters. They inspect
                      text in prompts, but don’t evaluate the multi-step chain
                      of tool actions. Clean prompts routinely emit authorized,
                      devastating tool invocations.
                    </p>
                  </div>
                </div>

                <div className="p-6 bg-red-50 border-l-4 border-red-600 rounded-r-xl my-8">
                  <div className="text-xs font-black text-red-600 uppercase tracking-widest mb-2">
                    The Fingerprint
                  </div>
                  <p className="text-red-900 mb-0 font-medium text-base">
                    OpenAI described the models as hyper-focused, spending
                    substantial inference compute in relentless pursuit of one
                    narrow goal. Humans wander; legitimate integrations repeat
                    predictable paths. Sustained, adaptive, goal-directed
                    behavior over hundreds of steps is the actual attack
                    signature.
                  </p>
                </div>
              </section>

              {/* Section 3: Defining the Agent's Job */}
              <section
                id="defining-agent-job"
                className="mb-12 pt-8 border-t border-slate-100"
              >
                <h2 className="text-3xl font-bold text-slate-800 mb-6">
                  Nobody Defined the Agent's Job
                </h2>
                <p>
                  Session-level detection is necessary, but stopping there
                  misses the more uncomfortable finding. The sandbox wasn’t
                  really a sandbox. An agent able to escalate, move laterally,
                  and egress was never contained in the first place.
                </p>
                <p>
                  The fundamental failure was that{" "}
                  <strong>
                    nobody had written down what the agent was permitted to do
                  </strong>
                  . It had a goal and an environment, and the environment’s
                  limits were assumed rather than declared. When there is no
                  declaration, every action gets judged purely on whether it is
                  technically permitted rather than whether it is in scope.
                </p>
              </section>

              {/* Section 4: An Agent Persona Defines the Job */}
              <section
                id="agent-persona-framework"
                className="mb-12 pt-8 border-t border-slate-100"
              >
                <h2 className="text-3xl font-bold text-slate-800 mb-6">
                  An Agent Persona Defines the Job
                </h2>
                <p>
                  An <strong>Agent Persona</strong> is a machine-readable
                  definition of what an agent is: its identity, the tools it can
                  invoke, the APIs and destinations it can reach, the skills it
                  can run, the models it can call, and the token budgets it
                  operates within.
                </p>

                {/* Figure 2 Image */}
                <div className="my-8">
                  <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-md bg-white">
                    <img
                      src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1788967254/after_this_Why_no_controdiagram1-1536x882_rhkdcy.png"
                      alt="Figure 2: An agent persona describes its job. Everything outside the description is denied."
                      className="w-full h-auto object-cover"
                    />
                    <div className="p-4 bg-slate-50 border-t border-slate-200">
                      <p className="text-xs font-semibold text-slate-600 mb-0">
                        Figure 2: An agent persona describes its job. Everything
                        outside the description is denied.
                      </p>
                    </div>
                  </div>
                </div>

                <p>
                  A persona defines what the agent is explicitly allowed to
                  execute; <strong>everything else is denied by default</strong>
                  . You never have to predict every exotic zero-day route—only
                  declare the legitimate scope of work. A zero-day gave these
                  models a clever new route, but into destinations that were
                  never inside the persona’s defined perimeter.
                </p>
              </section>

              {/* Section 5: Guardrails at the Gateway */}
              <section
                id="gateway-guardrails"
                className="mb-12 pt-8 border-t border-slate-100"
              >
                <h2 className="text-3xl font-bold text-slate-800 mb-6">
                  Guardrails Belong at the Gateway, Not Inside the Model
                </h2>
                <p>
                  This entire incident started with refusal behavior switched
                  off inside the benchmark runner. When safety lives strictly
                  inside model weights or system prompts, it is treated as a
                  config variable. It changes with a single line of code and
                  vanishes the moment an operator has a reason to disable it.
                </p>

                <div className="insight-box my-8">
                  <h4 className="text-emerald-400 mb-4 uppercase tracking-[0.15em] text-xs font-black">
                    Decoupled Gateway Enforcement
                  </h4>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    The security gateway inspects every prompt and tool call for
                    injection, jailbreak, and system extraction before requests
                    reach the provider. Internal agent instructions cannot
                    switch these controls off.
                  </p>
                  <div className="flex items-center gap-3 text-xs text-emerald-400 font-semibold bg-emerald-500/10 p-3 rounded-xl border border-emerald-500/20">
                    <Lock size={16} /> Survives Model Swaps: When teams rotate
                    models to optimize inference costs, gateway guardrails
                    guarantee uniform posture across every provider.
                  </div>
                </div>
              </section>

              {/* Section 6: Approved Models Only */}
              <section
                id="approved-models-registry"
                className="mb-12 pt-8 border-t border-slate-100"
              >
                <h2 className="text-3xl font-bold text-slate-800 mb-6">
                  Approved Models Only: The LLM Registry
                </h2>
                <p>
                  An agent’s model call is an outbound connection carrying full
                  application context—yet it frequently represents the least
                  governed channel in the enterprise.
                </p>
                <p>
                  An <strong>LLM Registry</strong> enforces that only vetted
                  models and registered endpoints can be queried. The gateway
                  brokers the true provider secrets so the autonomous agent
                  never directly holds or stores provider API credentials. Spend
                  ceilings and token limits are locked to each persona,
                  neutralizing token-theft and credential-replay chains at the
                  perimeter.
                </p>
              </section>

              {/* Section 7: The Five Gates */}
              <section
                id="five-gates-defense"
                className="mb-12 pt-8 border-t border-slate-100"
              >
                <h2 className="text-3xl font-bold text-slate-800 mb-6">
                  The Five-Gate Defense Architecture
                </h2>
                <p>
                  The models escaped through the single path they were permitted
                  to reach: a package caching proxy with zero contextual
                  governance. To prevent this, defense must be layered across
                  five independent enforcement checkpoints.
                </p>

                {/* Figure 3 Image */}
                <div className="my-8">
                  <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-md bg-white">
                    <img
                      src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1788967254/after_this_An_Agent_Persona_is_a_machinediagram2-1536x1195_tdux1o.png"
                      alt="Figure 3: Five gates, and the attack has to beat all five."
                      className="w-full h-auto object-cover"
                    />
                    <div className="p-4 bg-slate-50 border-t border-slate-200">
                      <p className="text-xs font-semibold text-slate-600 mb-0">
                        Figure 3: Five gates, and the attack has to beat all
                        five.
                      </p>
                    </div>
                  </div>
                </div>

                <p>
                  Getting past one gate provides zero help with the next; an
                  attack has to beat all five. Contrast this with traditional
                  point-in-time defenses, which had six separate opportunities
                  to intercept the Hugging Face intrusion and failed at every
                  checkpoint.
                </p>
                <p className="text-sm bg-slate-50 p-5 rounded-xl border border-slate-200 text-slate-600">
                  <strong>A Necessary Truth on Raw Egress:</strong> A gateway
                  inspects channels routed through it. An agent that spawns bash
                  processes to call <code>curl</code>, <code>git</code>, or
                  unproxied package fetchers is bypassing application-layer
                  hooks. Isolating uninstrumented host-level egress remains an
                  urgent frontier across the industry.
                </p>
              </section>

              {/* Section 8: Autonomous Containment */}
              <section
                id="autonomous-containment"
                className="mb-12 pt-8 border-t border-slate-100"
              >
                <h2 className="text-3xl font-bold text-slate-800 mb-6">
                  Detection Has to Be Able to Act on Its Own
                </h2>
                <p>
                  When Hugging Face’s team sought automated AI assistance for
                  emergency containment, leading commercial frontier models
                  refused the queries—unable to separate a defender reviewing
                  forensic attack traces from an adversary crafting exploits.
                </p>
                <p>
                  The forensics team was forced to spin up open-weight models
                  locally to investigate the incident. Offense operated
                  unconstrained at machine speed while defense remained
                  throttled behind vendor guardrails.
                </p>

                <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-2xl my-8">
                  <h4 className="font-bold text-emerald-950 mb-2 flex items-center gap-2">
                    <Cpu size={18} className="text-emerald-600" /> Overcoming
                    Human-in-the-Loop Latency
                  </h4>
                  <p className="text-slate-700 text-sm leading-relaxed mb-0">
                    If session-level anomaly detection requires human
                    confirmation before terminating an agent's token, an agent
                    executing thousands of programmatic requests will achieve
                    compromise long before the human opens the alert ticket.
                    Detection and revocation must be autonomous.
                  </p>
                </div>

                {/* Final Callout Banner */}
                {/* Final Callout Banner */}
                <div className="mt-16 not-prose">
                  <section className="relative bg-[#022722] rounded-[2rem] p-8 md:p-10 lg:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center shadow-2xl">
                    {/* LEFT CONTENT */}
                    <div className="md:col-span-6">
                      <div className="text-[11px] font-black text-emerald-400 uppercase tracking-[0.2em] mb-4">
                        Agentic Zero Trust
                      </div>

                      <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-white leading-tight mb-5 tracking-tight !mt-0">
                        Assess Your Agentic Attack Surface
                      </h2>

                      <p className="text-gray-300 text-base leading-[1.8] mb-10 max-w-2xl">
                        Evaluate your agent personas, MCP tool endpoints, and
                        gateway guardrail enforcement before autonomous agents
                        navigate outside their boundaries.
                      </p>

                      <Link
                        to="/contact"
                        className="inline-flex bg-[#048649] hover:bg-[#036c3a] text-white font-bold text-sm md:text-base px-7 py-3.5 rounded-full transition-all duration-300 w-fit no-underline shadow-lg shadow-green-950/20 hover:-translate-y-1"
                      >
                        Request Agent Security Review &rarr;
                      </Link>
                    </div>

                    {/* RIGHT IMAGE */}
                    <div className="md:col-span-6 flex justify-center items-center w-full">
                      <div className="w-full rounded-2xl overflow-hidden shadow-2xl shadow-green-950/10">
                        <img
                          src="https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1780214429/Ai_readiness_pxphkb.jpg"
                          alt="Agent Security Review Assessment"
                          className="w-full h-[250px] md:h-[350px] object-cover rounded-2xl"
                        />
                      </div>
                    </div>
                  </section>
                </div>
              </section>
            </main>
          </div>
        </div>

        <Footer />

        <style>{`
          .blog-content h2 { margin-top: 0; color: #0f172a; font-weight: 800; }
          .blog-content p { margin-bottom: 1.75rem; font-size: 1.125rem; line-height: 1.85; }

          .highlight-box {
            background: #f0faf8;
            border-left: 4px solid #10b981;
            padding: 2rem;
            border-radius: 0 1.5rem 1.5rem 0;
            margin: 2.5rem 0;
            color: #064e3b;
          }

          .insight-box {
            background: #0f172a;
            color: white;
            padding: 2.5rem;
            border-radius: 1.5rem;
            margin: 3rem 0;
          }

          html { scroll-behavior: smooth; }

          .no-scrollbar::-webkit-scrollbar { display: none; }
          .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>
      </div>
    </>
  );
};

export default AgenticAIGovernanceBlog;
